import { createIcons, ShieldAlert, FileText, Download, Printer, Settings, CheckCircle2, AlertTriangle, Eye, RefreshCw, AlertCircle, Clock, X, ArrowRight, History } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { router } from "../router/router.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { 
  buildMinutaDocumentModel, 
  compareRecommendationsWithMinutaSnapshot,
  type MinutaDiffResult,
  type MinutaDiffItem, parseRecommendationText } from "../../application/mappers/minuta-document-mapper.js";
import { buildMinutaSource, diffMinutaSource, sourceFromMinutaModel, type MinutaSourceSnapshot } from "../../domain/report/minuta-source.js";
import "../components/active-client-selector.js";
import "../components/document/pdf-preview.js";

class AppRecommendationsDraftPage extends HTMLElement {
  private selectedEquipmentId: string | null = null;
  private selectedInspectionId: string | null = null;
  /** P0-02: tipo de execução obrigatório para gerar/abrir Minuta (PARADA ou ROTINA). */
  private selectedExecutionType: 'PARADA' | 'ROTINA' | '' = '';
  
  private equipments: any[] = [];
  private inspections: any[] = [];
  
  private rawRecommendations: any[] = [];
  private snapshotData: any | null = null;
  
  private isLoading = false;
  private hasGenerated = false;
  
  private currentMinuta: any = null;
  private minutaRevisions: any[] = [];
  private showCorrectionModal = false;
  private correctionReason = '';
  private showHistoryModal = false;
  private minutaCorrections: any[] = [];
  
  // Edit State
  private showEditModal = false;
  private editingRec: any = null;
  private editingRecOriginalVersion = 0;

  // Diff & Update Revision State
  private diffResult: MinutaDiffResult | null = null;
  private showDiffModal = false;
  private showUpdateRevisionModal = false;
  private updateRevisionReason = 'Inclusão de nova(s) recomendação(ões)';
  private isUpdatingRevision = false;
  /** Motivo da falha de leitura dos equipamentos/inspeções (nunca uma lista vazia calada). */
  private loadError = "";

  connectedCallback() {
    const eqId = this.getAttribute('equipment-id');
    const inspId = this.getAttribute('inspection-id');
    if (eqId) this.selectedEquipmentId = eqId;
    if (inspId) this.selectedInspectionId = inspId;

    this.loadData();
    window.addEventListener('MASTER_DATA_UPDATED', () => this.loadData());
    window.addEventListener('ri_checklist_updated', () => this.loadData());
  }

  private async loadData() {
    this.isLoading = true;
    this.loadError = "";
    this.render();
    
    try {
      const p = (window as any).app?.platform;
      const ctx = sessionRequestContext();

      if (p?.equipmentService) {
        this.equipments = await p.equipmentService.listEquipments(ctx) || [];
      }
      
      // Pelo SERVIÇO (no servidor, o repositório do navegador não é o banco).
      if (p?.inspectionService) {
        this.inspections = await p.inspectionService.listAllInspections(ctx) || [];
      }

      const activeInspectionId = this.selectedInspectionId || this.inspections.find(i => i.equipmentId === this.selectedEquipmentId)?.id;

      // P0-02 V4 Etapa 1: loadData deve depender de inspectionId + selectedExecutionType.
      // Se executionType não estiver selecionado, não carrega Minuta (aguarda escolha do usuário).
      if (activeInspectionId && this.selectedExecutionType && p?.minutaService) {
        this.currentMinuta = await p.minutaService.getMinutaByInspectionAndExecutionType(
          ctx, activeInspectionId, this.selectedExecutionType
        );
        if (this.currentMinuta) {
          this.minutaRevisions = await p.minutaService.getRevisions(ctx, this.currentMinuta.id) || [];
          this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id) || [];
          
          // Find current revision snapshot
          const currentRev = this.minutaRevisions.find(r => r.status === 'ATUAL') || this.minutaRevisions[this.minutaRevisions.length - 1];
          if (currentRev && currentRev.snapshotData) {
            const rawCreated = currentRev.createdAt || this.currentMinuta.createdAt || new Date();
            this.snapshotData = {
              id: currentRev.snapshotId,
              type: 'MINUTA',
              revision: currentRev.revisionNumber,
              createdAt: rawCreated,
              data: currentRev.snapshotData,
              documentNumber: this.currentMinuta.trackingNumber
            };
            this.hasGenerated = true;
            console.log("[REAL-PDF-FLOW]", {
              module: "MINUTA",
              stage: "SNAPSHOT_RECEIVED",
              snapshotId: this.snapshotData.id,
              createdAt: this.snapshotData.createdAt,
              createdAtType: typeof this.snapshotData.createdAt
            });
          }
        }
      }

      // Fetch live recommendations and check diff
      if (this.selectedEquipmentId) {
        this.rawRecommendations = await this.fetchLiveRecommendations();
        if (this.currentMinuta && this.snapshotData) {
          this.checkMinutaDiff();
        }
      }

    } catch (err) {
      // Falha de leitura NÃO é "nenhum equipamento": a tela diz o motivo.
      console.error("Failed to load equipments/inspections", err);
      this.loadError = (err as { safeMessage?: string; message?: string })?.safeMessage || (err as Error)?.message || "Não foi possível carregar equipamentos e inspeções.";
    } finally {
      this.isLoading = false;
      this.render();
      // P0-02: auto-load só dispara se executionType já estiver selecionado.
      // Sem seleção explícita, a tela aguarda o usuário escolher PARADA/ROTINA.
      if (this.selectedEquipmentId && this.selectedExecutionType && !this.hasGenerated && !this.currentMinuta) {
        setTimeout(() => this.handleGenerateMinuta(), 100);
      }
    }
  }

  /**
   * FONTE DA VERDADE DA ATUALIZAÇÃO: hash determinístico do que entra na
   * Minuta (ids estáveis, texto, ZR, classificação, disciplina, dados
   * documentais, avaliação). "ATUALIZAÇÃO NECESSÁRIA" só quando o hash atual
   * difere de minuta.lastSourceHash — nunca por updatedAt, ordem, F5 ou
   * reabertura. Com RI vinculado, a fonte é a mesma do Workspace de Engenharia.
   */
  private checkMinutaDiff() {
    if (!this.snapshotData || !this.currentMinuta) {
      this.diffResult = null;
      return;
    }
    void this.checkMinutaDiffAsync();
  }

  private async checkMinutaDiffAsync() {
    if (!this.snapshotData || !this.currentMinuta) return;
    const p = (window as any).app?.platform;
    const ctx = { activeClientId: contextStore.get().activeClientId, userId: contextStore.get().userId, permissions: contextStore.get().permissions, professionalId: contextStore.get().professionalId || null } as any;
    let updateNeeded = false;
    let counts = { newCount: 0, modifiedCount: 0, removedCount: 0, pendingCount: 0, totalSnapshot: 0 };
    let items: MinutaDiffItem[] = [];
    try {
      const report = p?.reportService?.getReportByInspection ? await p.reportService.getReportByInspection(ctx, this.currentMinuta.inspectionId).catch(() => null) : null;
      if (report && p?.engineeringService?.getMinutaTecnicaContext) {
        const mt = await p.engineeringService.getMinutaTecnicaContext(ctx, report.id);
        updateNeeded = mt.updateNeeded;
        counts = { newCount: mt.diff.newCount, modifiedCount: mt.diff.modifiedCount, removedCount: mt.diff.removedCount, pendingCount: mt.diff.pendingCount, totalSnapshot: mt.diff.unchangedCount + mt.diff.modifiedCount + mt.diff.removedCount };
        items = mt.updateNeeded ? mt.diff.items.filter((i: any) => i.type !== "INALTERADA").map((i: any) => this.toDiffItem(i)) : [];
        if (mt.minuta) this.currentMinuta = mt.minuta;
      } else {
        const current = await this.buildLocalSource();
        const previous: MinutaSourceSnapshot = this.currentMinuta.lastSourceSnapshot || sourceFromMinutaModel(this.snapshotData.data || this.snapshotData, "", this.currentMinuta.currentRevision, new Date().toISOString());
        const diff = diffMinutaSource(previous, current);
        updateNeeded = diff.updateNeeded;
        counts = { newCount: diff.newCount, modifiedCount: diff.modifiedCount, removedCount: diff.removedCount, pendingCount: diff.pendingCount, totalSnapshot: previous.recommendations.length };
        items = diff.updateNeeded ? diff.items.filter(i => i.type !== "INALTERADA").map(i => this.toDiffItem(i)) : [];
        if (!this.currentMinuta.lastSourceHash && p?.minutaService?.saveSource) {
          // Minuta anterior ao hash: grava a base agora, para nunca "renascer" o que já foi incorporado.
          this.currentMinuta = await p.minutaService.saveSource(ctx, this.currentMinuta.id, previous);
        }
      }
    } catch (e) {
      console.warn("[MINUTA] diff pela fonte indisponível", e);
      return;
    }
    this.diffResult = { hasChanges: updateNeeded, totalCurrent: this.rawRecommendations.length, totalSnapshot: counts.totalSnapshot, newCount: counts.newCount, modifiedCount: counts.modifiedCount, removedCount: counts.removedCount, pendingCount: counts.pendingCount, items };
    if (updateNeeded && this.currentMinuta.status === 'EMITIDA' && p?.minutaService) {
      this.currentMinuta.status = 'ATUALIZAÇÃO PENDENTE';
      p.minutaService.markUpdatePending(ctx, this.currentMinuta.id, { newCount: counts.newCount, modifiedCount: counts.modifiedCount }).catch(() => {});
    }
    this.render();
  }

  private toDiffItem(i: any): MinutaDiffItem {
    const rec = i.current || i.previous || {};
    const firstLine = parseRecommendationText(rec.text || "").title || rec.componentName || "Recomendação Técnica";
    return { type: i.type === "NOVA" ? "NEW" : i.type === "ALTERADA" ? "MODIFIED" : "REMOVED", id: i.id, discipline: rec.discipline || "CALDEIRARIA", componentName: rec.componentName || "Componente", firstLine, zr: rec.zr || "—", origin: "Checklist de Inspeção", changes: i.fields && i.fields.length ? i.fields : undefined };
  }

  /** Fonte local (sem RI vinculado): recomendações vivas + dados documentais desta página. */
  private async buildLocalSource(): Promise<MinutaSourceSnapshot> {
    const contextData = await this.buildContextMetadata();
    return buildMinutaSource({
      reportId: "", reportRevision: this.currentMinuta?.currentRevision || "00", recommendations: this.rawRecommendations, assessment: null,
      documentary: { clientName: contextData.clientName, projectName: contextData.projectName, unitArea: contextData.unitArea, osNumber: contextData.osNumber, tag: contextData.tag, equipmentType: contextData.equipmentType, fabricationStandard: contextData.fabricationStandard, inspectorName: contextData.inspectorName }
    });
  }

  private async fetchLiveRecommendations(): Promise<any[]> {
    const p = (window as any).app?.platform;
    const ctx = { 
      activeClientId: contextStore.get().activeClientId,
      userId: contextStore.get().userId,
      permissions: contextStore.get().permissions
    } as any;

    const reports = await p?.reportService?.listReports(ctx).catch(() => []) || [];
    const allRecs: any[] = [];
    const seenIds = new Set<string>();

    try {
      const directRecs = await p?.reportService?.listAllRecommendations(ctx) || [];
      for (const rec of directRecs) {
        if (!seenIds.has(rec.id)) {
          seenIds.add(rec.id);
          allRecs.push(rec);
        }
      }
    } catch (err) {}

    for (const report of reports) {
      const r = report as any;
      if (r.checklistData) {
        for (const item of r.checklistData) {
          if (item.recommendations) {
            item.recommendations.forEach((rec: any, index: number) => {
              const recKey = rec.id || `rec-rep-${r.id}-${index}`;
              if (!seenIds.has(recKey)) {
                seenIds.add(recKey);
                allRecs.push({ ...rec, id: recKey, reportId: r.id, inspectionId: r.inspectionId, equipmentId: r.equipmentSnapshotId || r.equipmentId, componentName: item.name });
              }
            });
          }
        }
      }
    }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('ri_checklist_insp_')) {
        const inspId = key.replace('ri_checklist_insp_', '');
        try {
          const items = JSON.parse(localStorage.getItem(key) || '[]');
          if (Array.isArray(items)) {
            items.forEach(item => {
              if (item.recommendations && Array.isArray(item.recommendations)) {
                item.recommendations.forEach((rec: any) => {
                  if (rec.id && !seenIds.has(rec.id)) {
                    seenIds.add(rec.id);
                    allRecs.push({ ...rec, id: rec.id, inspectionId: inspId, componentName: item.name });
                  }
                });
              }
            });
          }
        } catch {}
      }
    }

    // Filter context
    const eqInspections = this.inspections.filter(x => x.equipmentId === this.selectedEquipmentId);
    const eqInspectionIds = new Set(eqInspections.map(i => i.id));
    const eqReports = reports.filter((r: any) => r.equipmentSnapshotId === this.selectedEquipmentId || r.equipmentId === this.selectedEquipmentId || eqInspectionIds.has(r.inspectionId));
    const eqReportIds = new Set(eqReports.map((r: any) => r.id));

    let filtered = allRecs.filter(r => {
      const matchesEq = r.equipmentId === this.selectedEquipmentId;
      const matchesInsp = eqInspectionIds.has(r.inspectionId);
      const matchesRep = eqReportIds.has(r.integrityReportId) || eqReportIds.has(r.reportId);
      return matchesEq || matchesInsp || matchesRep;
    });

    if (this.selectedInspectionId) {
      filtered = filtered.filter(r => r.inspectionId === this.selectedInspectionId);
    }

    return filtered;
  }

  /**
   * O snapshot da Minuta nasce sem Cliente; o Equipamento a que ela pertence
   * o conhece. Sem isso, com a sessão em "Todos os Clientes", o motor de
   * documentos recusa a geração ("No client identified").
   */
  private withOwningClient(snapshot: any): any {
    if (!snapshot || snapshot.clientId) return snapshot;
    const equipmentId = this.selectedEquipmentId || this.currentMinuta?.equipmentId;
    const equipment = this.equipments.find(e => e.id === equipmentId);
    const clientId = equipment?.clientId;
    return clientId ? { ...snapshot, clientId } : snapshot;
  }

  /** Logo documental da versão visual PUBLICADA (null = texto INSPECT IA de sempre). */
  private async publishedDocumentLogo(): Promise<string | null> {
    try {
      const c = contextStore.get();
      const ctx = { activeClientId: c.activeClientId || undefined, permissions: c.permissions, correlationId: "", userId: c.userId, professionalId: c.professionalId || null };
      const visual = await (window as any).app?.platform?.studioDesignService?.resolveDocumentVisual(ctx, "RI", "minuta-nova", false);
      return visual?.logoImage ?? null;
    } catch {
      return null;
    }
  }

  private async buildContextMetadata() {
    const p = (window as any).app?.platform;
    const ctx = sessionRequestContext();

    const equipment = this.equipments.find(e => e.id === this.selectedEquipmentId);
    let clientName = equipment?.clientName || 'N/A';
    if (p?.adminService) {
      try {
        const clients = await p.adminService.listClients(ctx);
        const c = clients.find((c: any) => c.id === equipment?.clientId);
        if (c) clientName = c.fantasyName || c.name || c.corporateName;
      } catch {}
    }

    const inspection = this.inspections.find(i => i.id === this.selectedInspectionId);
    let projectName = 'N/A';
    let unitArea = 'N/A';
    
    const targetProjectId = inspection?.projectId || equipment?.projectId;
    const targetUnitAreaId = inspection?.unitAreaId || equipment?.unitAreaId || equipment?.areaId;

    if (p?.projectService) {
      try {
        if (targetProjectId) {
           const projects = await p.projectService.listProjects(undefined, undefined, ctx);
           const proj = projects.find((x: any) => x.id === targetProjectId);
           if (proj) {
              const code = proj.code ? String(proj.code).trim() : '';
              const name = proj.name ? String(proj.name).trim() : '';
              const parts = [code, name].filter(Boolean);
              projectName = parts.length > 0 ? parts.join(' - ') : (proj.projectName || proj.id);
           }
        }
        if (targetUnitAreaId) {
           const areas = await p.projectService.listUnitAreas(undefined, undefined, ctx);
           const area = areas.find((x: any) => x.id === targetUnitAreaId);
           if (area) {
              const code = area.code ? String(area.code).trim() : '';
              const name = area.name ? String(area.name).trim() : '';
              const parts = [code, name].filter(Boolean);
              unitArea = parts.length > 0 ? parts.join(' - ') : (area.unitAreaName || area.area || area.id);
           }
        }
      } catch {}
    }

    if (projectName === 'N/A') {
       projectName = inspection?.projectName || inspection?.projectCode || equipment?.projectName || equipment?.projectCode || 'N/A';
    }
    
    if (unitArea === 'N/A') {
       unitArea = inspection?.unitAreaName || inspection?.area || equipment?.unitAreaName || equipment?.area || equipment?.location || 'N/A';
    }

    let inspectorName = inspection?.inspectorName;
    if (!inspectorName && inspection?.inspectorId && p?.adminService) {
      try {
        const professionals = await p.adminService.listProfessionals(ctx);
        const prof = professionals.find((x: any) => x.id === inspection.inspectorId);
        if (prof) {
          inspectorName = prof.name || prof.fullName || prof.id;
        }
      } catch {}
    }
    inspectorName = inspectorName || 'Inspetor Responsável';
    const projectCode = equipment?.projectCode || equipment?.constructionCode || equipment?.properties?.constructionCode || 'N/A';
    const fabricationStandard = equipment?.fabricationStandard || equipment?.projectStandard || equipment?.properties?.designConstructionCode || equipment?.inServiceInspectionCode || 'N/A';
    const fabricationCode = equipment?.fabricationCode || equipment?.properties?.fabricationCode || 'N/A';
    // Melhoria 012 (Minuta definitiva): SPEC e Sistema do Equipamento real — nunca inventados quando ausentes.
    const materialSpec = equipment?.spec || equipment?.materialSpecification || equipment?.properties?.spec || 'N/A';
    // P0-03: quando o equipamento É um Sistema de Tubulação (PIPING_SYSTEM),
    // o campo `system` pode estar vazio — o próprio tag/name identifica o sistema.
    const systemName = (equipment?.system && equipment.system !== 'NÃO APLICÁVEL' && equipment.system !== 'NÃO INFORMADO')
      ? equipment.system
      : (equipment?.equipmentType === 'PIPING_SYSTEM' ? (equipment?.tag || equipment?.name || 'N/A') : 'N/A');

    const typeMap: Record<string, string> = {
      'PIPING_LINE': 'TUBULAÇÃO',
      'PIPING_SYSTEM': 'SISTEMA DE TUBULAÇÃO',
      'PRESSURE_VESSEL': 'VASO DE PRESSÃO',
      'STORAGE_TANK': 'TANQUE DE ARMAZENAMENTO',
      'HEAT_EXCHANGER': 'TROCADOR DE CALOR',
      'BOILER': 'CALDEIRA',
      'FURNACE': 'FORNO',
      'OTHER': 'EQUIPAMENTO'
    };
    const rawType = equipment?.equipmentType || 'N/A';
    const mappedType = typeMap[rawType] || equipment?.equipmentTypeDisplay || rawType;

    return {
      clientName,
      projectName,
      unitArea,
      osNumber: equipment?.osNumber || inspection?.osNumber || 'N/A',
      tag: equipment?.tag || 'N/A',
      equipmentType: mappedType,
      projectCode,
      fabricationCode,
      fabricationStandard,
      materialSpec,
      systemName,
      inspectorName,
      // STUDIO INSPECT IA: logo documental publicado agora — congela no snapshot da revisão criada.
      tenantLogo: await this.publishedDocumentLogo(),
      trackingNumber: this.currentMinuta?.trackingNumber,
      revisionNumber: this.currentMinuta ? this.currentMinuta.currentRevision : '00'
    };
  }

  private async handleGenerateMinuta() {
    if (!this.selectedEquipmentId) {
      alert("Selecione um equipamento para gerar a minuta.");
      return;
    }
    // P0-02: executionType é obrigatório. Bloquear chamada ao backend sem seleção explícita.
    if (!this.selectedExecutionType || (this.selectedExecutionType !== 'PARADA' && this.selectedExecutionType !== 'ROTINA')) {
      alert("Selecione o tipo de execução da minuta: PARADA ou ROTINA.");
      return;
    }

    this.isLoading = true;
    this.render();

    try {
      const p = (window as any).app?.platform;
      const ctx = {
        activeClientId: contextStore.get().activeClientId,
        userId: contextStore.get().userId,
        permissions: contextStore.get().permissions
      } as any;

      const recommendations = await this.fetchLiveRecommendations();
      if (recommendations.length === 0) {
        alert("Nenhuma recomendação foi localizada para o equipamento e a inspeção selecionados.");
        this.hasGenerated = false;
        this.snapshotData = null;
        return;
      }

      // P0-02 V3: filtrar recomendações ANTES de construir model/source/snapshot.
      // O snapshot persistido deve conter SOMENTE recomendações do tipo selecionado.
      const normalizeExec = (r: any): 'PARADA' | 'ROTINA' => {
        const raw = (r.executeIn || r.executionType || r.execution || '').toString().toUpperCase();
        if (raw === 'SHUTDOWN' || raw === 'PARADA') return 'PARADA';
        return 'ROTINA';
      };
      const filteredRecommendations = recommendations.filter(r => normalizeExec(r) === this.selectedExecutionType);

      if (filteredRecommendations.length === 0) {
        alert(`Não há recomendações do tipo ${this.selectedExecutionType} para o contexto selecionado.`);
        this.hasGenerated = false;
        this.snapshotData = null;
        return;
      }
      this.rawRecommendations = filteredRecommendations;
      const contextData = await this.buildContextMetadata();
      // P0-02 V4 Etapa 13/14: inspectionId e projectId REAIS são obrigatórios.
      // NÃO usar INSP-DEFAULT nem fallback 'PRJ'/projectCode como identidade documental.
      const insp = this.selectedInspectionId
        ? this.inspections.find(i => i.id === this.selectedInspectionId)
        : this.inspections.find(i => i.equipmentId === this.selectedEquipmentId);
      if (!insp?.id) {
        alert("Não foi possível identificar a inspeção. Selecione uma inspeção válida antes de gerar a Minuta.");
        this.isLoading = false;
        this.render();
        return;
      }
      if (!insp.projectId) {
        alert("A inspeção selecionada não possui Projeto vinculado. Vincule um Projeto à inspeção antes de gerar a Minuta oficial.");
        this.isLoading = false;
        this.render();
        return;
      }
      const effectiveProjectId = insp.projectId;

      const model = buildMinutaDocumentModel(contextData, filteredRecommendations);

      if (p?.minutaService) {
        const source = buildMinutaSource({ reportId: "", reportRevision: "00", recommendations: filteredRecommendations, assessment: null, documentary: { clientName: contextData.clientName, projectName: contextData.projectName, unitArea: contextData.unitArea, osNumber: contextData.osNumber, tag: contextData.tag, equipmentType: contextData.equipmentType, fabricationStandard: contextData.fabricationStandard, inspectorName: contextData.inspectorName } });

        // P0-02 V4 Etapa 13: inspectionId real já validado acima; INSP-DEFAULT eliminado.
        const res = await p.minutaService.createInitialMinuta(
          ctx,
          this.selectedEquipmentId,
          insp.id,
          effectiveProjectId,
          model,
          source,
          this.selectedExecutionType
        );

        this.currentMinuta = res.minuta;
        this.snapshotData = res.snapshot;
        this.minutaRevisions = await p.minutaService.getRevisions(ctx, this.currentMinuta.id);
        this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id);
      } else {
        // Fallback local apenas para preview quando service não está disponível (modo dev/browser-only).
        // Em backend mode real, p.minutaService sempre existe; se falhar, o catch abaixo exibirá erro.
        this.snapshotData = {
          id: `MINUTA-${Date.now()}`,
          type: 'MINUTA',
          revision: '00',
          createdAt: new Date(),
          data: model
        };
      }
      
      this.hasGenerated = true;
      this.checkMinutaDiff();

    } catch (err: any) {
      console.error(err);
      alert(err.message || "Erro ao gerar minuta.");
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  private async handleConfirmUpdateRevision() {
    if (!this.currentMinuta) return;

    this.isUpdatingRevision = true;
    this.render();

    try {
      const p = (window as any).app?.platform;
      const ctx = {
        activeClientId: contextStore.get().activeClientId,
        userId: contextStore.get().userId,
        permissions: contextStore.get().permissions
      } as any;

      const reason = this.updateRevisionReason && this.updateRevisionReason.trim()
        ? this.updateRevisionReason.trim()
        : 'Inclusão de nova(s) recomendação(ões)';

      const report = p?.reportService?.getReportByInspection
        ? await p.reportService.getReportByInspection(ctx, this.currentMinuta.inspectionId).catch(() => null)
        : null;

      // P0-02 V3: updateMinutaFromReport do EngineeringService agrupa internamente por executionType
      // e atualiza apenas a Minuta correspondente ao tipo da minuta corrente.
      // O selectedExecutionType já está vinculado à currentMinuta.executionType carregada.
      if (report && p?.engineeringService?.updateMinutaFromReport) {
        const mt = await p.engineeringService.updateMinutaFromReport(ctx, report.id, reason);
        if (mt.minuta) {
          this.currentMinuta = mt.minuta;
        }
        this.minutaRevisions = await p.minutaService.getRevisions(ctx, this.currentMinuta.id);
        this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id);
        const currentRev = this.minutaRevisions.find((r: any) => r.status === 'ATUAL') || this.minutaRevisions[this.minutaRevisions.length - 1];
        if (currentRev?.snapshotData) {
          this.snapshotData = {
            id: currentRev.snapshotId,
            type: 'MINUTA',
            revision: currentRev.revisionNumber,
            createdAt: currentRev.createdAt || new Date(),
            data: currentRev.snapshotData,
            documentNumber: this.currentMinuta.trackingNumber
          };
        }
      } else {
        // 1. Reload live recommendations freshly (Controle de concorrência)
        const freshRecs = await this.fetchLiveRecommendations();
        this.rawRecommendations = freshRecs;

        const currentRevNum = parseInt(this.currentMinuta.currentRevision, 10);
        const nextRevNum = (currentRevNum + 1).toString().padStart(2, '0');

        const contextData = await this.buildContextMetadata();
        contextData.revisionNumber = nextRevNum;

        // 2. Build model preserving lines, carry-forward recommendations and discipline ordering from current snapshot
        const model = buildMinutaDocumentModel(contextData, freshRecs, this.snapshotData);

        // 3. Create update revision
        if (p?.minutaService) {
          const source = buildMinutaSource({ reportId: "", reportRevision: "00", recommendations: freshRecs, assessment: null, documentary: { clientName: contextData.clientName, projectName: contextData.projectName, unitArea: contextData.unitArea, osNumber: contextData.osNumber, tag: contextData.tag, equipmentType: contextData.equipmentType, fabricationStandard: contextData.fabricationStandard, inspectorName: contextData.inspectorName } });
          const res = await p.minutaService.createUpdateRevision(ctx, this.currentMinuta.id, model, reason, source);
          this.currentMinuta = res.minuta;
          this.snapshotData = res.snapshot;
          this.minutaRevisions = await p.minutaService.getRevisions(ctx, this.currentMinuta.id);
          this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id);
        }
      }

      this.showUpdateRevisionModal = false;
      this.showDiffModal = false;
      this.hasGenerated = true;

      // Re-evaluate diff (should now be 0 pending)
      this.checkMinutaDiff();

      window.dispatchEvent(new CustomEvent('MASTER_DATA_UPDATED'));

      alert(`Revisão ${this.currentMinuta.currentRevision} gerada com sucesso! O documento oficial foi atualizado.`);

    } catch (err: any) {
      console.error(err);
      alert(err.message || "Erro ao gerar atualização da minuta.");
    } finally {
      this.isUpdatingRevision = false;
      this.render();
    }
  }

  render() {
    const isReady = !this.isLoading && !this.isUpdatingRevision;
    const canRequestRevision = contextStore.get()?.permissions.has(PermissionCode.DocumentRevise);

    const eqOptions = this.equipments.map(e => 
      `<option value="${e.id}" ${e.id === this.selectedEquipmentId ? 'selected' : ''}>${e.tag} - ${e.equipmentTypeDisplay || e.equipmentType}</option>`
    ).join('');

    const activeEqInspections = this.inspections.filter(i => i.equipmentId === this.selectedEquipmentId);
    const inspOptions = activeEqInspections.map(i => 
      `<option value="${i.id}" ${i.id === this.selectedInspectionId ? 'selected' : ''}>${new Date(i.date || i.createdAt).toLocaleDateString('pt-BR')} - ${i.modality || i.inspectionType}</option>`
    ).join('');

    const isEmbedded = this.hasAttribute('equipment-id');
    const hasDiffChanges = this.diffResult && this.diffResult.hasChanges;
    const isPendingUpdate = this.currentMinuta && (this.currentMinuta.status === 'ATUALIZAÇÃO PENDENTE' || hasDiffChanges);

    this.innerHTML = `
      <style>
        .minuta-container {
          padding: ${isEmbedded ? '0' : '24px'};
          max-width: 1400px;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          gap: 20px;
          height: ${isEmbedded ? '100%' : 'calc(100vh - 60px)'};
        }
        .minuta-header {
          display: ${isEmbedded ? 'none' : 'flex'};
          justify-content: space-between;
          align-items: center;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--color-border-subtle);
        }
        .minuta-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-text-primary);
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .minuta-controls {
          display: flex;
          flex-direction: column;
          gap: 16px;
          background: var(--color-surface-secondary);
          padding: 16px;
          border-radius: var(--radius-md);
          border: 1px solid var(--color-border-default);
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
          min-width: 250px;
        }
        .form-group label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }
        .form-select {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-default);
          color: var(--color-text-primary);
          padding: 8px 12px;
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
          outline: none;
          transition: border-color var(--transition-fast);
        }
        .form-select:focus {
          border-color: var(--color-primary);
        }
        .btn-primary {
          background: var(--ia-action-bg, var(--color-primary));
          color: var(--ia-action-fg, #F7F8F8);
          border: none;
          padding: 8px 16px;
          border-radius: var(--radius-sm);
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          height: 35px;
          transition: background var(--transition-fast);
        }
        .btn-primary:hover {
          background: var(--color-primary-hover);
        }
        .btn-primary:disabled {
          background: var(--color-graphite-600);
          cursor: not-allowed;
        }
        .pdf-viewer-container {
          flex: 1;
          background: var(--color-surface-elevated);
          border: 1px solid var(--color-border-default);
          border-radius: var(--radius-lg);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .empty-state {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: var(--color-text-muted);
          gap: 12px;
        }

        /* Banner de Atualização Necessária */
        .minuta-alert-banner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: #fffbeb;
          border: 1px solid #fde68a;
          border-left: 5px solid #d97706;
          padding: 14px 20px;
          border-radius: var(--radius-md);
          box-shadow: 0 2px 4px rgba(217, 119, 6, 0.08);
          gap: 16px;
          flex-wrap: wrap;
        }
        .alert-icon-wrap {
          background: #fef3c7;
          width: 42px;
          height: 42px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .btn-primary-banner {
          background: #d97706;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.15s ease;
        }
        .btn-primary-banner:hover {
          background: #b45309;
        }
        .btn-secondary-banner {
          background: white;
          color: #92400e;
          border: 1px solid #fcd34d;
          padding: 8px 14px;
          border-radius: 6px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          transition: background 0.15s ease;
        }
        .btn-secondary-banner:hover {
          background: #fef3c7;
        }

        /* Contadores KPI */
        .minuta-kpi-bar {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
          gap: 12px;
          width: 100%;
        }
        .kpi-mini-card {
          background: var(--color-surface-primary);
          border: 1px solid var(--color-border-subtle);
          border-radius: 6px;
          padding: 10px 14px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .kpi-mini-card.highlight-new {
          border-color: #86efac;
          background: #f0fdf4;
        }
        .kpi-mini-card.highlight-mod {
          border-color: #fde68a;
          background: #fffbeb;
        }
        .kpi-mini-card.highlight-pending {
          border-color: #fcd34d;
          background: #fffbeb;
        }
        .kpi-mini-label {
          font-size: 0.7rem;
          font-weight: 700;
          color: var(--color-text-secondary);
          text-transform: uppercase;
        }
        .kpi-mini-value {
          font-size: 1.25rem;
          font-weight: 800;
          color: var(--color-text-primary);
        }
        .highlight-new .kpi-mini-value {
          color: #166534;
        }
        .highlight-pending .kpi-mini-value {
          color: #b45309;
        }
      </style>
      
      <div class="minuta-container">
        <div class="minuta-header">
          <div class="minuta-title">
            <i data-lucide="file-text" style="color: var(--ia-primary);"></i>
            Minuta de Recomendações
          </div>
          <div style="font-size: 0.85rem; color: var(--color-text-secondary);">
            Geração de Rascunho para Análise Técnica
          </div>
        </div>

        ${hasDiffChanges ? `
          <div class="minuta-alert-banner">
            <div style="display: flex; align-items: center; gap: 14px;">
              <div class="alert-icon-wrap">
                <i data-lucide="alert-triangle" style="width: 24px; height: 24px; color: var(--ia-warning-fg, #d97706);"></i>
              </div>
              <div>
                <h4 style="margin: 0; font-size: 0.95rem; font-weight: 800; color: #92400e; letter-spacing: 0.5px;">
                  ⚠ ATUALIZAÇÃO DA MINUTA NECESSÁRIA
                </h4>
                <p style="margin: 3px 0 0 0; font-size: 0.875rem; color: #b45309;">
                  ${(this.diffResult?.newCount || 0) > 0 || (this.diffResult?.modifiedCount || 0) > 0
                    ? `Foram identificadas: <strong>${this.diffResult?.newCount} nova(s) recomendação(ões)</strong>${(this.diffResult?.modifiedCount || 0) > 0 ? ` e <strong>${this.diffResult?.modifiedCount} alterada(s)</strong>` : ''} após a geração da <strong>REV. ${this.currentMinuta.currentRevision}</strong>.`
                    : `Foram identificadas alterações nos dados documentais ou na avaliação de engenharia após a geração da <strong>REV. ${this.currentMinuta.currentRevision}</strong>.`}
                </p>
              </div>
            </div>
            <div style="display: flex; gap: 10px; align-items: center;">
              <button id="btn-view-changes" class="btn-secondary-banner">
                <i data-lucide="eye" style="width: 14px; height: 14px;"></i> VER ALTERAÇÕES
              </button>
              ${this.diffResult?.hasChanges ? `
              <button id="btn-open-update-revision" class="btn-primary-banner">
                <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i> GERAR ATUALIZAÇÃO DA MINUTA
              </button>
              ` : `
              <span style="font-size: 0.8rem; color: var(--color-text-secondary); font-weight: 500; padding: 6px 12px;">Minuta atualizada — nenhuma alteração pendente.</span>
              `}
            </div>
          </div>
        ` : ''}

        ${this.loadError ? `<div role="alert" style="padding: 10px 14px; border-radius: 6px; margin-bottom: 12px; border: 1px solid var(--status-danger-border); background: var(--status-danger-bg); color: var(--color-text-primary); font-size: 0.85rem;"><b>Não foi possível carregar equipamentos e inspeções:</b> ${this.loadError.replace(/[<>&"]/g, ch => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[ch]!))}</div>` : ''}

        <div class="minuta-controls">
          <div style="display: flex; flex-direction: column; gap: 12px; width: 100%;">
            <div style="display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap;">
              <div class="form-group">
                <label>Equipamento / Ativo</label>
                <select id="select-equipment" class="form-select" ${!isReady ? 'disabled' : ''}>
                  <option value="">Selecione o equipamento...</option>
                  ${eqOptions}
                </select>
              </div>
              <div class="form-group">
                <label>Inspeção Referência (Opcional)</label>
                <select id="select-inspection" class="form-select" ${!this.selectedEquipmentId || !isReady ? 'disabled' : ''}>
                  <option value="">Todas as recomendações ativas...</option>
                  ${inspOptions}
                </select>
              </div>
              <div class="form-group">
                <label>Execução da Minuta <span style="color: var(--status-danger-text, #dc2626);">*</span></label>
                <select id="select-execution-type" class="form-select" ${!this.selectedEquipmentId || !isReady ? 'disabled' : ''}>
                  <option value="">Selecione o tipo de execução...</option>
                  <option value="PARADA">PARADA</option>
                  <option value="ROTINA">ROTINA</option>
                </select>
                <small style="color: var(--color-text-secondary); font-size: 0.75rem;">Obrigatório para gerar a minuta.</small>
              </div>
              <!-- P0-02 V5 Bloco A: botão habilitado se NÃO existir Minuta DO TIPO SELECIONADO.
                   Uma Minuta ROTINA existente NÃO impede criação da PARADA (e vice-versa).
                   currentMinuta já é filtrado por executionType no loadData/selector change. -->
              <button id="btn-generate" class="btn-primary" style="background: ${this.currentMinuta ? 'var(--color-graphite-600)' : 'var(--ia-action-bg, var(--color-primary))'}" ${!this.selectedEquipmentId || !isReady || !this.selectedExecutionType || this.currentMinuta ? 'disabled' : ''}>
                ${this.isLoading ? 'Processando...' : `<i data-lucide="printer"></i> GERAR MINUTA ${this.selectedExecutionType || ''}`}
              </button>
            </div>
            
            ${this.currentMinuta ? `
            <div style="display: flex; align-items: center; justify-content: space-between; padding-top: 12px; border-top: 1px solid var(--color-border-subtle); flex-wrap: wrap; gap: 12px;">
              <div style="display: flex; gap: 24px; align-items: center; flex-wrap: wrap;">
                <!-- P0-02 V5 Bloco B: Identificação documental clara MINUTA — PARADA / ROTINA -->
                <div style="display: flex; flex-direction: column;">
                  <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 600;">Documento</span>
                  <span style="font-size: 0.95rem; font-weight: 800; color: var(--ia-action-bg, var(--color-primary)); letter-spacing: 0.3px;">MINUTA — ${this.currentMinuta.executionType || this.selectedExecutionType || 'N/A'}</span>
                </div>
                <div style="display: flex; flex-direction: column; padding-left: 20px; border-left: 1px solid var(--color-border-subtle);">
                  <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 600;">Nº</span>
                  <span style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-primary);">${this.currentMinuta.trackingNumber}</span>
                </div>
                <div style="display: flex; flex-direction: column; padding-left: 20px; border-left: 1px solid var(--color-border-subtle);">
                  <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 600;">Revisão</span>
                  <span style="font-size: 0.875rem; font-weight: 700; color: var(--color-text-primary);">REV. ${this.currentMinuta.currentRevision}</span>
                </div>
                <div style="display: flex; flex-direction: column; padding-left: 20px; border-left: 1px solid var(--color-border-subtle);">
                  <span style="font-size: 0.75rem; color: var(--color-text-secondary); font-weight: 600;">Status</span>
                  <span style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 0.75rem; font-weight: 700; 
                    background: ${isPendingUpdate ? '#fef3c7' : (this.currentMinuta.status === 'EMITIDA' ? '#dcfce7' : (this.currentMinuta.status === 'EM CORREÇÃO' ? '#fee2e2' : '#f3f4f6'))};
                    color: ${isPendingUpdate ? '#92400e' : (this.currentMinuta.status === 'EMITIDA' ? '#166534' : (this.currentMinuta.status === 'EM CORREÇÃO' ? '#991b1b' : '#374151'))};">
                    ${isPendingUpdate ? 'ATUALIZAÇÃO PENDENTE' : this.currentMinuta.status}
                  </span>
                </div>
              </div>
              <div style="display: flex; gap: 8px;">
                <button id="btn-history" style="padding: 6px 12px; font-size: 0.85rem; background: white; color: var(--color-text-inverse); border: 1px solid var(--color-border-default); border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 600;">
                   <i data-lucide="history" style="width: 14px; height: 14px;"></i> Histórico de Revisões (${this.minutaRevisions.length})
                </button>
                ${this.currentMinuta.status === 'EMITIDA' && !hasDiffChanges && canRequestRevision ? `
                  <button id="btn-request-correction" style="padding: 6px 12px; font-size: 0.85rem; background: #fffbeb; color: #b45309; border: 1px solid #fde68a; border-radius: 4px; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 600;">
                    Solicitar Correção
                  </button>
                ` : ''}
              </div>
            </div>

            <!-- Contador KPI Canônico (Regra 32) -->
            <div class="minuta-kpi-bar" style="margin-top: 8px; padding-top: 12px; border-top: 1px solid var(--color-border-subtle);">
              <div class="kpi-mini-card">
                <span class="kpi-mini-label">RECOMENDAÇÕES DA REV ATUAL</span>
                <span class="kpi-mini-value">${this.diffResult ? this.diffResult.totalSnapshot : (this.snapshotData?.data?.groups ? this.snapshotData.data.groups.reduce((acc: number, g: any) => acc + (g.recommendations?.length || 0), 0) : 0)}</span>
              </div>
              <div class="kpi-mini-card">
                <span class="kpi-mini-label">RECOMENDAÇÕES DO CHECKLIST</span>
                <span class="kpi-mini-value">${this.rawRecommendations.length}</span>
              </div>
              <div class="kpi-mini-card ${this.diffResult && this.diffResult.newCount > 0 ? 'highlight-new' : ''}">
                <span class="kpi-mini-label">NOVAS</span>
                <span class="kpi-mini-value">${this.diffResult ? this.diffResult.newCount : 0}</span>
              </div>
              <div class="kpi-mini-card ${this.diffResult && this.diffResult.modifiedCount > 0 ? 'highlight-mod' : ''}">
                <span class="kpi-mini-label">ALTERADAS</span>
                <span class="kpi-mini-value">${this.diffResult ? this.diffResult.modifiedCount : 0}</span>
              </div>
              <div class="kpi-mini-card ${this.diffResult && this.diffResult.pendingCount > 0 ? 'highlight-pending' : ''}">
                <span class="kpi-mini-label">PENDENTES</span>
                <span class="kpi-mini-value">${this.diffResult ? this.diffResult.pendingCount : 0}</span>
              </div>
            </div>
            ` : ''}
          </div>
        </div>

        <div class="pdf-viewer-container">
          ${this.currentMinuta && this.currentMinuta.status === 'EM CORREÇÃO' ? `
            <div style="background: #fffbeb; border-bottom: 1px solid #fde68a; padding: 16px;">
              <h4 style="font-size: 1rem; font-weight: 600; color: #b45309; margin: 0 0 12px 0;">Correções Solicitadas</h4>
              <div style="display: flex; flex-direction: column; gap: 8px;">
                ${this.minutaCorrections.filter((c: any) => c.status !== 'VALIDADA').length === 0 ? `
                  <div style="font-size: 0.875rem; color: #166534; font-weight: 500; display: flex; align-items: center; gap: 8px;">
                    <i data-lucide="check-circle-2" style="width: 16px; height: 16px;"></i>
                    Todas as correções foram validadas. Você pode gerar uma nova revisão.
                  </div>
                ` : ''}
                ${this.minutaCorrections.map((c: any) => `
                  <div style="display: flex; justify-content: space-between; align-items: center; background: white; padding: 12px; border-radius: 6px; border: 1px solid #fde68a;">
                    <div>
                      <div style="font-size: 0.75rem; font-weight: 700; color: var(--color-text-secondary);">ITEM ${c.visualIndex}</div>
                      <div style="font-size: 0.875rem; font-weight: 500; color: var(--color-text-primary); margin-top: 4px;">${c.reason}</div>
                      <div style="font-size: 0.75rem; color: var(--color-text-muted); margin-top: 4px;">Status: ${c.status}</div>
                    </div>
                    <div>
                      ${c.status === 'CORRIGIDA' && canRequestRevision ? `
                        <button class="btn-validate-correction" data-id="${c.id}" style="padding: 6px 12px; font-size: 0.75rem; background: var(--ia-success-bg, #166534); color: white; border: none; border-radius: 4px; cursor: pointer; font-weight: 600;">Validar Correção</button>
                      ` : ''}
                    </div>
                  </div>
                `).join('')}
              </div>
            </div>
          ` : ''}
          ${this.hasGenerated && this.snapshotData ? 
            '<app-pdf-preview id="minuta-pdf-preview" style="flex: 1; display: block;"></app-pdf-preview>'
          : 
            '<div class="empty-state"><i data-lucide="eye" style="width: 48px; height: 48px; color: var(--color-graphite-600);"></i><span style="font-weight: 500;">Selecione o contexto e clique em "Gerar Minuta" para visualizar o documento oficial.</span></div>'
          }
        </div>
      </div>
      
      ${this.renderDiffModal()}
      ${this.renderUpdateRevisionModal()}
      ${this.renderCorrectionModal()}
      ${this.renderEditModal()}
      ${this.renderHistoryModal()}
    `;

    createIcons({
      icons: { ShieldAlert, FileText, Download, Printer, Settings, CheckCircle2, AlertTriangle, Eye, RefreshCw, AlertCircle, Clock, X, ArrowRight, History },
      root: this
    });

    if (this.hasGenerated && this.snapshotData) {
      const preview = this.querySelector('#minuta-pdf-preview') as any;
      if (preview) {
        preview.snapshot = this.withOwningClient(this.snapshotData);
      }
    }

    this.attachEvents();
  }

  private renderDiffModal() {
    if (!this.showDiffModal || !this.diffResult) return '';

    const nextRevNum = this.currentMinuta 
      ? (parseInt(this.currentMinuta.currentRevision, 10) + 1).toString().padStart(2, '0') 
      : '01';

    return `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 50; display: flex; align-items: center; justify-content: center; background: rgba(17, 24, 39, 0.5); backdrop-filter: blur(4px);">
        <div style="background: white; --color-text-primary: var(--color-text-inverse); --color-text-secondary: var(--color-text-inverse-muted); --color-text-muted: var(--color-text-inverse-muted); border-radius: 8px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); width: 100%; max-width: 52rem; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh;">
          <div style="padding: 16px 24px; border-bottom: 1px solid var(--color-border-subtle); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-secondary);">
            <div>
              <h3 style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary); margin: 0; display: flex; align-items: center; gap: 8px;">
                <i data-lucide="eye" style="width: 18px; height: 18px; color: var(--ia-warning-fg, #d97706);"></i>
                Alterações Identificadas no Checklist de Inspeção
              </h3>
              <div style="font-size: 0.8rem; color: var(--color-text-secondary); margin-top: 4px;">
                Comparação entre o Checklist atual e o Snapshot da <strong>REV. ${this.currentMinuta?.currentRevision || '00'}</strong>
              </div>
            </div>
            <button id="btn-close-diff" style="background: none; border: none; cursor: pointer; color: var(--color-text-muted); font-size: 1.5rem;">
              &times;
            </button>
          </div>
          
          <div style="padding: 20px 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 14px;">
            ${this.diffResult.items.length === 0 ? `
              <div style="text-align: center; padding: 30px; color: var(--color-text-secondary);">
                Nenhuma alteração pendente encontrada. O checklist está em total conformidade com a minuta.
              </div>
            ` : `
              <div style="display: flex; gap: 12px; margin-bottom: 8px; font-size: 0.85rem; color: var(--color-text-secondary);">
                <span>Total de pendências: <strong>${this.diffResult.pendingCount}</strong></span>
                <span>•</span>
                <span style="color: #166534;">Novas: <strong>${this.diffResult.newCount}</strong></span>
                <span>•</span>
                <span style="color: #92400e;">Alteradas: <strong>${this.diffResult.modifiedCount}</strong></span>
              </div>

              <div style="display: flex; flex-direction: column; gap: 12px;">
                ${this.diffResult.items.map((item, idx) => `
                  <div style="border: 1px solid var(--color-border-default); border-radius: 6px; padding: 14px; background: ${item.type === 'NEW' ? '#f0fdf4' : '#fffbeb'};">
                    <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 8px;">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span style="display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 0.72rem; font-weight: 800; 
                          background: ${item.type === 'NEW' ? '#dcfce7' : '#fef3c7'}; 
                          color: ${item.type === 'NEW' ? '#166534' : '#92400e'};">
                          ${item.type === 'NEW' ? 'NOVA RECOMENDAÇÃO' : (item.type === 'MODIFIED' ? 'RECOMENDAÇÃO ALTERADA' : 'REMOVIDA')}
                        </span>
                        <span style="font-size: 0.8rem; font-weight: 700; color: var(--color-text-secondary);">
                          Disciplina: ${item.discipline}
                        </span>
                      </div>
                      <span style="font-size: 0.75rem; color: var(--color-text-muted);">
                        Origem: ${item.origin}
                      </span>
                    </div>

                    <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 8px;">
                      <div>
                        <span style="font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; text-transform: uppercase;">Componente</span>
                        <div style="font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary);">${item.componentName}</div>
                      </div>
                      <div>
                        <span style="font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; text-transform: uppercase;">ZR</span>
                        <div style="font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary);">${item.zr}</div>
                      </div>
                    </div>

                    <div>
                      <span style="font-size: 0.72rem; color: var(--color-text-muted); font-weight: 600; text-transform: uppercase;">Primeira Linha (Título)</span>
                      <div style="font-size: 0.875rem; color: var(--color-text-primary); font-weight: 500; margin-top: 2px;">
                        ${item.firstLine}
                      </div>
                    </div>

                    ${item.changes && item.changes.length > 0 ? `
                      <div style="margin-top: 8px; padding-top: 8px; border-top: 1px dashed #fcd34d; font-size: 0.75rem; color: #b45309;">
                        <strong>Campos alterados:</strong> ${item.changes.join(', ')}
                      </div>
                    ` : ''}
                  </div>
                `).join('')}
              </div>
            `}
          </div>

          <div style="padding: 16px 24px; border-top: 1px solid var(--color-border-subtle); background: var(--color-surface-secondary); display: flex; justify-content: flex-end; gap: 12px;">
            <button id="btn-close-diff-action" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: var(--color-text-inverse); background: white; border: 1px solid var(--color-border-default); border-radius: 6px; cursor: pointer;">
              Fechar
            </button>
            <button id="btn-proceed-update-revision" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 700; color: white; background: #d97706; border: none; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 8px;">
              <i data-lucide="refresh-cw" style="width: 14px; height: 14px;"></i>
              GERAR ATUALIZAÇÃO DA MINUTA (REV. ${nextRevNum})
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderUpdateRevisionModal() {
    if (!this.showUpdateRevisionModal || !this.currentMinuta) return '';

    const currentRevNum = parseInt(this.currentMinuta.currentRevision, 10);
    const nextRevNum = (currentRevNum + 1).toString().padStart(2, '0');

    return `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 60; display: flex; align-items: center; justify-content: center; background: rgba(17, 24, 39, 0.5); backdrop-filter: blur(4px);">
        <div style="background: white; --color-text-primary: var(--color-text-inverse); --color-text-secondary: var(--color-text-inverse-muted); --color-text-muted: var(--color-text-inverse-muted); border-radius: 8px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); width: 100%; max-width: 38rem; overflow: hidden; display: flex; flex-direction: column;">
          <div style="padding: 16px 24px; border-bottom: 1px solid var(--color-border-subtle); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-secondary);">
            <h3 style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary); margin: 0; display: flex; align-items: center; gap: 8px;">
              <i data-lucide="refresh-cw" style="width: 18px; height: 18px; color: var(--ia-primary);"></i>
              ATUALIZAR MINUTA
            </h3>
            <button id="btn-close-update-revision" style="background: none; border: none; cursor: pointer; color: var(--color-text-muted); font-size: 1.5rem;">
              &times;
            </button>
          </div>

          <div style="padding: 24px; display: flex; flex-direction: column; gap: 16px;">
            <div style="background: var(--color-surface-secondary); border: 1px solid var(--color-border-default); border-radius: 6px; padding: 16px; display: grid; grid-template-columns: 1fr 1fr; gap: 14px;">
              <div>
                <span style="font-size: 0.72rem; color: var(--color-text-secondary); font-weight: 600; text-transform: uppercase;">Nº DA MINUTA</span>
                <div style="font-size: 0.95rem; font-weight: 700; color: var(--color-text-primary); margin-top: 2px;">${this.currentMinuta.trackingNumber}</div>
              </div>
              <div>
                <span style="font-size: 0.72rem; color: var(--color-text-secondary); font-weight: 600; text-transform: uppercase;">EVOLUÇÃO DA REVISÃO</span>
                <div style="font-size: 0.95rem; font-weight: 800; color: var(--ia-primary); margin-top: 2px;">
                  REV. ${this.currentMinuta.currentRevision} &rarr; REV. ${nextRevNum}
                </div>
              </div>
              <div>
                <span style="font-size: 0.72rem; color: var(--color-text-secondary); font-weight: 600; text-transform: uppercase;">NOVAS RECOMENDAÇÕES</span>
                <div style="font-size: 1.125rem; font-weight: 800; color: #166534; margin-top: 2px;">${this.diffResult?.newCount || 0}</div>
              </div>
              <div>
                <span style="font-size: 0.72rem; color: var(--color-text-secondary); font-weight: 600; text-transform: uppercase;">RECOMENDAÇÕES ALTERADAS</span>
                <div style="font-size: 1.125rem; font-weight: 800; color: #b45309; margin-top: 2px;">${this.diffResult?.modifiedCount || 0}</div>
              </div>
            </div>

            <div style="font-size: 0.8rem; color: var(--color-text-secondary); line-height: 1.4;">
              A revisão <strong>REV. ${this.currentMinuta.currentRevision}</strong> permanecerá salva no histórico e preservada de forma imutável. Todos os itens existentes manterão sua numeração original.
            </div>

            <div class="form-group" style="width: 100%;">
              <label>Motivo da Revisão *</label>
              <input type="text" id="txt-update-revision-reason" value="${this.updateRevisionReason}" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 10px 12px; font-size: 0.875rem; outline: none;" placeholder="Ex: Inclusão de nova(s) recomendação(ões)..." />
            </div>
          </div>

          <div style="padding: 16px 24px; border-top: 1px solid var(--color-border-subtle); background: var(--color-surface-secondary); display: flex; justify-content: flex-end; gap: 12px;">
            <button id="btn-cancel-update-revision" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: var(--color-text-inverse); background: white; border: 1px solid var(--color-border-default); border-radius: 6px; cursor: pointer;" ${this.isUpdatingRevision ? 'disabled' : ''}>
              Cancelar
            </button>
            <button id="btn-confirm-execute-revision" style="padding: 8px 18px; font-size: 0.875rem; font-weight: 700; color: var(--ia-action-fg, #F7F8F8); background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; cursor: pointer; display: flex; align-items: center; gap: 8px;" ${this.isUpdatingRevision ? 'disabled' : ''}>
              ${this.isUpdatingRevision ? 'Gerando...' : `GERAR REV. ${nextRevNum}`}
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCorrectionModal() {
    if (!this.showCorrectionModal) return '';
    const recs = this.snapshotData?.data?.recommendations || [];
    
    return `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 50; display: flex; align-items: center; justify-content: center; background: rgba(17, 24, 39, 0.5); backdrop-filter: blur(4px);">
        <div style="background: white; --color-text-primary: var(--color-text-inverse); --color-text-secondary: var(--color-text-inverse-muted); --color-text-muted: var(--color-text-inverse-muted); border-radius: 8px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); width: 100%; max-width: 48rem; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh;">
          <div style="padding: 16px 24px; border-bottom: 1px solid var(--color-border-subtle); display: flex; justify-content: space-between; align-items: center;">
            <h3 style="font-size: 1.125rem; font-weight: 500; color: var(--color-text-primary); margin: 0;">Analisar Minuta (Correção)</h3>
            <button id="btn-close-correction" style="background: none; border: none; cursor: pointer; color: var(--color-text-muted); font-size: 1.5rem;">
              &times;
            </button>
          </div>
          <div style="padding: 24px; overflow-y: auto;">
            <p style="font-size: 0.875rem; color: var(--color-text-secondary); margin-bottom: 16px;">Escolha corrigir diretamente a recomendação ou marque para enviar ao inspetor solicitando correção.</p>
            
            <div id="correction-items-list" style="display: flex; flex-direction: column; gap: 16px;">
              ${recs.map((rec: any, index: number) => `
                <div class="correction-item-row" data-id="${rec.id}" data-index="${index + 1}" style="border: 1px solid var(--color-border-default); border-radius: 6px; padding: 12px;">
                  <div style="display: flex; gap: 12px; align-items: flex-start;">
                    <input type="checkbox" class="correction-checkbox" style="margin-top: 4px;" />
                    <div style="flex: 1;">
                      <div style="font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); margin-bottom: 4px;">${index + 1}. ${rec.componentName || 'Componente'}</div>
                      <div style="font-size: 0.75rem; color: var(--color-text-secondary); margin-bottom: 8px;">${rec.description}</div>
                      
                      <div style="display: flex; gap: 8px; margin-top: 12px;">
                        <button type="button" class="btn-corrigir-agora" data-id="${rec.id}" style="padding: 4px 12px; font-size: 0.75rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 4px; cursor: pointer;">
                          CORRIGIR AGORA
                        </button>
                      </div>

                      <textarea class="correction-item-reason" style="margin-top: 12px; width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none; display: none;" rows="2" placeholder="Motivo detalhado para enviar ao Inspetor..."></textarea>
                    </div>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
          <div style="padding: 16px 24px; border-top: 1px solid var(--color-border-subtle); background: var(--color-surface-secondary); display: flex; justify-content: flex-end; gap: 12px;">
            <button id="btn-cancel-correction" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: var(--color-text-inverse); background: white; border: 1px solid var(--color-border-default); border-radius: 6px; cursor: pointer;">
              Cancelar
            </button>
            <button id="btn-confirm-correction" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: white; background: #d97706; border: none; border-radius: 6px; cursor: pointer;" disabled>
              Enviar ao Inspetor
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private async openEditModal(recId: string | null) {
    if (!recId) return;
    const recs = this.snapshotData?.data?.recommendations || [];
    const rec = recs.find((r: any) => r.id === recId);
    if (rec) {
      this.editingRec = { ...rec };
      this.editingRecOriginalVersion = rec.version || 0;
      this.showEditModal = true;
      this.showCorrectionModal = false;
      this.render();
    }
  }

  private async saveEngineeringEdit() {
    if (!this.editingRec) return;
    
    try {
      const scope = (this.querySelector('#edit-rec-scope') as HTMLTextAreaElement)?.value;
      const location = (this.querySelector('#edit-rec-location') as HTMLInputElement)?.value;
      const dimension = (this.querySelector('#edit-rec-dimension') as HTMLInputElement)?.value;
      const discipline = (this.querySelector('#edit-rec-discipline') as HTMLSelectElement)?.value;
      const criticality = (this.querySelector('#edit-rec-criticality') as HTMLSelectElement)?.value;

      const payload = {
        scope,
        location,
        dimension,
        discipline,
        criticality,
        version: this.editingRecOriginalVersion,
        correctionSource: 'ENGINEERING'
      };

      await (window as any).app.platform.reportService?.updateRecommendationFull(
        { activeClientId: contextStore.get().activeClientId, userId: contextStore.get().userId, permissions: contextStore.get().permissions } as any,
        this.editingRec.id,
        payload as any
      );

      this.showEditModal = false;
      this.editingRec = null;
      
      alert('Recomendação atualizada com sucesso no Checklist original. A minuta será recarregada.');
      this.isLoading = true;
      this.render();
      await this.handleGenerateMinuta();
      
    } catch (err: any) {
      alert(err.message || 'Erro ao salvar correção.');
    }
  }

  private renderEditModal() {
    if (!this.showEditModal || !this.editingRec) return '';
    return `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 60; display: flex; align-items: center; justify-content: center; background: rgba(17, 24, 39, 0.5); backdrop-filter: blur(4px);">
        <div style="background: white; --color-text-primary: var(--color-text-inverse); --color-text-secondary: var(--color-text-inverse-muted); --color-text-muted: var(--color-text-inverse-muted); border-radius: 8px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); width: 100%; max-width: 48rem; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh;">
          <div style="padding: 16px 24px; border-bottom: 1px solid var(--color-border-subtle); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-secondary);">
            <div>
              <h3 style="font-size: 1.125rem; font-weight: 600; color: var(--color-text-primary); margin: 0;">Corrigir Recomendação</h3>
              <div style="font-size: 0.75rem; color: var(--ia-warning-fg, #d97706); font-weight: 600; margin-top: 4px;">
                Esta alteração será aplicada ao Checklist de Inspeção e refletirá automaticamente na Minuta.
              </div>
            </div>
            <button id="btn-close-edit" style="background: none; border: none; cursor: pointer; color: var(--color-text-muted); font-size: 1.5rem;">
              &times;
            </button>
          </div>
          <div style="padding: 24px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px;">
            <div style="display: flex; gap: 16px;">
              <div class="form-group" style="flex: 1;">
                <label>Componente / Título</label>
                <input type="text" value="${this.editingRec.componentName || ''}" disabled style="background: var(--color-surface-primary); border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; color: var(--color-text-secondary);" />
              </div>
              <div class="form-group" style="flex: 1;">
                <label>ZR</label>
                <input type="text" value="${this.editingRec.zr || ''}" disabled style="background: var(--color-surface-primary); border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; color: var(--color-text-secondary);" />
              </div>
            </div>

            <div class="form-group">
              <label>Descritivo (Escopo)</label>
              <textarea id="edit-rec-scope" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none; min-height: 100px;">${this.editingRec.scope || this.editingRec.description || ''}</textarea>
            </div>

            <div style="display: flex; gap: 16px;">
              <div class="form-group" style="flex: 1;">
                <label>Localização</label>
                <input id="edit-rec-location" type="text" value="${this.editingRec.location || ''}" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none;" />
              </div>
              <div class="form-group" style="flex: 1;">
                <label>Dimensão</label>
                <input id="edit-rec-dimension" type="text" value="${this.editingRec.dimension || ''}" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none;" />
              </div>
            </div>

            <div style="display: flex; gap: 16px;">
              <div class="form-group" style="flex: 1;">
                <label>Disciplina</label>
                <select id="edit-rec-discipline" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none;">
                  <option value="CALDEIRARIA" ${this.editingRec.discipline === 'CALDEIRARIA' ? 'selected' : ''}>CALDEIRARIA</option>
                  <option value="TUBULAÇÃO" ${this.editingRec.discipline === 'TUBULAÇÃO' ? 'selected' : ''}>TUBULAÇÃO</option>
                  <option value="PINTURA" ${this.editingRec.discipline === 'PINTURA' ? 'selected' : ''}>PINTURA</option>
                  <option value="MECÂNICA" ${this.editingRec.discipline === 'MECÂNICA' ? 'selected' : ''}>MECÂNICA</option>
                  <option value="CIVIL" ${this.editingRec.discipline === 'CIVIL' ? 'selected' : ''}>CIVIL</option>
                  <option value="INSTRUMENTAÇÃO" ${this.editingRec.discipline === 'INSTRUMENTAÇÃO' ? 'selected' : ''}>INSTRUMENTAÇÃO</option>
                  <option value="ISOLAMENTO TÉRMICO" ${this.editingRec.discipline === 'ISOLAMENTO TÉRMICO' || this.editingRec.discipline === 'ISOLAMENTO' ? 'selected' : ''}>ISOLAMENTO TÉRMICO</option>
                </select>
              </div>
              <div class="form-group" style="flex: 1;">
                <label>Criticidade</label>
                <select id="edit-rec-criticality" style="width: 100%; box-sizing: border-box; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 8px; font-size: 0.875rem; outline: none;">
                  <option value="CRÍTICA" ${this.editingRec.criticality === 'CRÍTICA' || this.editingRec.criticality === 'CRITICAL' ? 'selected' : ''}>CRÍTICA</option>
                  <option value="ALTA" ${this.editingRec.criticality === 'ALTA' || this.editingRec.criticality === 'HIGH' ? 'selected' : ''}>ALTA</option>
                  <option value="MÉDIA" ${this.editingRec.criticality === 'MÉDIA' || this.editingRec.criticality === 'MODERATE' ? 'selected' : ''}>MÉDIA</option>
                  <option value="BAIXA" ${this.editingRec.criticality === 'BAIXA' || this.editingRec.criticality === 'LOW' ? 'selected' : ''}>BAIXA</option>
                </select>
              </div>
            </div>
          </div>
          <div style="padding: 16px 24px; border-top: 1px solid var(--color-border-subtle); background: var(--color-surface-secondary); display: flex; justify-content: flex-end; gap: 12px;">
            <button id="btn-cancel-edit" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: var(--color-text-inverse); background: white; border: 1px solid var(--color-border-default); border-radius: 6px; cursor: pointer;">
              Cancelar
            </button>
            <button id="btn-save-edit" style="padding: 8px 16px; font-size: 0.875rem; font-weight: 500; color: var(--ia-action-fg, #F7F8F8); background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; cursor: pointer;">
              Salvar Correção
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderHistoryModal() {
    if (!this.showHistoryModal) return '';
    return `
      <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; z-index: 50; display: flex; align-items: center; justify-content: center; background: rgba(17, 24, 39, 0.5); backdrop-filter: blur(4px);">
        <div style="background: white; --color-text-primary: var(--color-text-inverse); --color-text-secondary: var(--color-text-inverse-muted); --color-text-muted: var(--color-text-inverse-muted); border-radius: 8px; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); width: 100%; max-width: 48rem; overflow: hidden; display: flex; flex-direction: column; max-height: 80vh;">
          <div style="padding: 16px 24px; border-bottom: 1px solid var(--color-border-subtle); display: flex; justify-content: space-between; align-items: center; background: var(--color-surface-secondary);">
            <div>
              <h3 style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-primary); margin: 0; display: flex; align-items: center; gap: 8px;">
                <i data-lucide="history" style="width: 18px; height: 18px; color: var(--ia-primary);"></i>
                Histórico de Revisões da Minuta
              </h3>
              <div style="font-size: 0.8rem; color: var(--color-text-secondary); margin-top: 2px;">
                Nº ${this.currentMinuta?.trackingNumber || '—'}
              </div>
            </div>
            <button id="btn-close-history" style="background: none; border: none; cursor: pointer; color: var(--color-text-muted); font-size: 1.5rem;">
              &times;
            </button>
          </div>
          <div style="padding: 0; overflow-y: auto;">
            <table style="min-width: 100%; border-collapse: collapse;">
              <thead style="background: var(--color-surface-secondary);">
                <tr>
                  <th style="padding: 12px 20px; text-align: left; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">REV</th>
                  <th style="padding: 12px 20px; text-align: left; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">Data</th>
                  <th style="padding: 12px 20px; text-align: left; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">Status</th>
                  <th style="padding: 12px 20px; text-align: left; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">Recomendações</th>
                  <th style="padding: 12px 20px; text-align: left; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">Motivo</th>
                  <th style="padding: 12px 20px; text-align: right; font-size: 0.75rem; font-weight: 600; color: var(--color-text-muted); text-transform: uppercase;">Ação</th>
                </tr>
              </thead>
              <tbody>
                ${this.minutaRevisions.map((rev: any) => {
                  const revRecCount = rev.snapshotData?.groups 
                    ? rev.snapshotData.groups.reduce((sum: number, g: any) => sum + (g.recommendations?.length || 0), 0)
                    : (rev.snapshotData?.recommendations?.length || '—');

                  return `
                    <tr style="border-bottom: 1px solid var(--color-border-subtle);">
                      <td style="padding: 14px 20px; font-size: 0.875rem; font-weight: 700; color: var(--color-text-primary);">REV. ${rev.revisionNumber}</td>
                      <td style="padding: 14px 20px; font-size: 0.85rem; color: var(--color-text-secondary);">${new Date(rev.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td style="padding: 14px 20px; white-space: nowrap;">
                        <span style="display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 0.72rem; font-weight: 700; background: ${rev.status === 'ATUAL' ? '#dcfce7' : '#f3f4f6'}; color: ${rev.status === 'ATUAL' ? '#166534' : '#374151'};">
                          ${rev.status}
                        </span>
                      </td>
                      <td style="padding: 14px 20px; font-size: 0.85rem; font-weight: 600; color: var(--color-text-primary);">${revRecCount}</td>
                      <td style="padding: 14px 20px; font-size: 0.85rem; color: var(--color-text-secondary); max-width: 220px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;" title="${rev.correctionReason || 'Emissão Inicial'}">
                        ${rev.correctionReason || 'Emissão Inicial'}
                      </td>
                      <td style="padding: 14px 20px; text-align: right; font-size: 0.85rem; font-weight: 600;">
                        <button class="btn-view-historical" data-id="${rev.id}" style="color: var(--ia-primary); background: none; border: 1px solid var(--color-border-default); border-radius: 4px; padding: 4px 10px; cursor: pointer; display: inline-flex; align-items: center; gap: 4px;">
                          <i data-lucide="eye" style="width: 12px; height: 12px;"></i> Ver PDF
                        </button>
                      </td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  private attachEvents() {
    const selectEq = this.querySelector('#select-equipment') as HTMLSelectElement;
    const selectInsp = this.querySelector('#select-inspection') as HTMLSelectElement;
    const btnGenerate = this.querySelector('#btn-generate') as HTMLButtonElement;

    if (selectEq) {
      selectEq.addEventListener('change', async (e) => {
        this.selectedEquipmentId = (e.target as HTMLSelectElement).value;
        this.selectedInspectionId = null;
        this.currentMinuta = null;
        this.minutaRevisions = [];
        this.diffResult = null;
        this.hasGenerated = false;
        this.loadData();
      });
    }

    if (selectInsp) {
      selectInsp.addEventListener('change', async (e) => {
        this.selectedInspectionId = (e.target as HTMLSelectElement).value;
        this.hasGenerated = false;
        this.loadData();
      });
    }

    // P0-02 V4 Etapa 1: seletor obrigatório de execução da Minuta (PARADA/ROTINA).
    // Ao trocar o tipo, limpar estado atual e recarregar loadData para buscar a Minuta correspondente.
    const selectExecType = this.querySelector('#select-execution-type') as HTMLSelectElement;
    if (selectExecType) {
      selectExecType.value = this.selectedExecutionType || '';
      selectExecType.addEventListener('change', (e) => {
        this.selectedExecutionType = (e.target as HTMLSelectElement).value as 'PARADA' | 'ROTINA' | '';
        // Limpar estado da Minuta anterior para evitar contaminação entre tipos
        this.currentMinuta = null;
        this.snapshotData = null;
        this.minutaRevisions = [];
        this.minutaCorrections = [];
        this.diffResult = null;
        this.hasGenerated = false;
        // Recarregar dados com o novo executionType
        this.loadData();
      });
    }

    if (btnGenerate) {
      btnGenerate.addEventListener('click', () => {
        this.handleGenerateMinuta();
      });
    }

    // Diff Modal triggers
    const btnViewChanges = this.querySelector('#btn-view-changes');
    if (btnViewChanges) {
      btnViewChanges.addEventListener('click', () => {
        this.showDiffModal = true;
        this.render();
      });
    }

    const btnCloseDiff = this.querySelector('#btn-close-diff');
    if (btnCloseDiff) {
      btnCloseDiff.addEventListener('click', () => {
        this.showDiffModal = false;
        this.render();
      });
    }

    const btnCloseDiffAction = this.querySelector('#btn-close-diff-action');
    if (btnCloseDiffAction) {
      btnCloseDiffAction.addEventListener('click', () => {
        this.showDiffModal = false;
        this.render();
      });
    }

    // Open Update Revision Confirmation Modal
    const btnOpenUpdateRevision = this.querySelector('#btn-open-update-revision');
    if (btnOpenUpdateRevision) {
      btnOpenUpdateRevision.addEventListener('click', () => {
        this.updateRevisionReason = 'Inclusão de nova(s) recomendação(ões)';
        this.showUpdateRevisionModal = true;
        this.render();
      });
    }

    const btnProceedUpdateRevision = this.querySelector('#btn-proceed-update-revision');
    if (btnProceedUpdateRevision) {
      btnProceedUpdateRevision.addEventListener('click', () => {
        this.showDiffModal = false;
        this.updateRevisionReason = 'Inclusão de nova(s) recomendação(ões)';
        this.showUpdateRevisionModal = true;
        this.render();
      });
    }

    const btnCloseUpdateRevision = this.querySelector('#btn-close-update-revision');
    if (btnCloseUpdateRevision) {
      btnCloseUpdateRevision.addEventListener('click', () => {
        this.showUpdateRevisionModal = false;
        this.render();
      });
    }

    const btnCancelUpdateRevision = this.querySelector('#btn-cancel-update-revision');
    if (btnCancelUpdateRevision) {
      btnCancelUpdateRevision.addEventListener('click', () => {
        this.showUpdateRevisionModal = false;
        this.render();
      });
    }

    const txtReason = this.querySelector('#txt-update-revision-reason') as HTMLInputElement;
    if (txtReason) {
      txtReason.addEventListener('input', (e) => {
        this.updateRevisionReason = (e.target as HTMLInputElement).value;
      });
    }

    const btnConfirmExecuteRevision = this.querySelector('#btn-confirm-execute-revision');
    if (btnConfirmExecuteRevision) {
      btnConfirmExecuteRevision.addEventListener('click', () => {
        this.handleConfirmUpdateRevision();
      });
    }

    const btnHistory = this.querySelector('#btn-history') as HTMLButtonElement;
    if (btnHistory) {
      btnHistory.addEventListener('click', () => {
        this.showHistoryModal = true;
        this.render();
      });
    }

    const btnCorrection = this.querySelector('#btn-request-correction') as HTMLButtonElement;
    if (btnCorrection) {
      btnCorrection.addEventListener('click', () => {
        this.showCorrectionModal = true;
        this.render();
      });
    }

    const btnCloseCorrection = this.querySelector('#btn-close-correction') as HTMLButtonElement;
    const btnCancelCorrection = this.querySelector('#btn-cancel-correction') as HTMLButtonElement;
    const btnConfirmCorrection = this.querySelector('#btn-confirm-correction') as HTMLButtonElement;
    
    if (btnCloseCorrection) btnCloseCorrection.addEventListener('click', () => { this.showCorrectionModal = false; this.render(); });
    if (btnCancelCorrection) btnCancelCorrection.addEventListener('click', () => { this.showCorrectionModal = false; this.render(); });
    
    const itemRows = this.querySelectorAll('.correction-item-row');
    const getActiveRequests = () => {
      const reqs: any[] = [];
      itemRows.forEach(row => {
        const checkbox = row.querySelector('.correction-checkbox') as HTMLInputElement;
        const textarea = row.querySelector('.correction-item-reason') as HTMLTextAreaElement;
        if (checkbox && checkbox.checked && textarea.value.trim()) {
          reqs.push({
            recommendationId: row.getAttribute('data-id'),
            visualIndex: row.getAttribute('data-index'),
            reason: textarea.value.trim()
          });
        }
      });
      return reqs;
    };

    const updateConfirmButton = () => {
      if (btnConfirmCorrection) {
        const reqs = getActiveRequests();
        btnConfirmCorrection.disabled = reqs.length === 0;
      }
    };

    itemRows.forEach(row => {
      const checkbox = row.querySelector('.correction-checkbox') as HTMLInputElement;
      const textarea = row.querySelector('.correction-item-reason') as HTMLTextAreaElement;
      if (checkbox && textarea) {
        checkbox.addEventListener('change', () => {
          textarea.style.display = checkbox.checked ? 'block' : 'none';
          updateConfirmButton();
        });
        textarea.addEventListener('input', updateConfirmButton);
      }
    });

    if (btnConfirmCorrection) {
      btnConfirmCorrection.addEventListener('click', () => {
        this.handleRequestCorrection(getActiveRequests());
      });
    }

    const btnCloseHistory = this.querySelector('#btn-close-history') as HTMLButtonElement;
    if (btnCloseHistory) btnCloseHistory.addEventListener('click', () => { this.showHistoryModal = false; this.render(); });

    const validateButtons = this.querySelectorAll('.btn-validate-correction');
    validateButtons.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.target as HTMLElement).getAttribute('data-id');
        if (id) {
          const ctx = contextStore.get() as any;
          const p = (window as any).app?.platform;
          if (p?.minutaService && this.currentMinuta) {
            await p.minutaService.updateCorrectionStatus(ctx, this.currentMinuta.id, id, 'VALIDADA');
            this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id);
            this.render();
          }
        }
      });
    });

    const historyButtons = this.querySelectorAll('.btn-view-historical');
    historyButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        this.viewHistoricalPdf(id);
      });
    });

    this.querySelectorAll('.btn-corrigir-agora').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        this.openEditModal(id);
      });
    });

    const btnCloseEdit = this.querySelector('#btn-close-edit');
    if (btnCloseEdit) {
      btnCloseEdit.addEventListener('click', () => {
        this.showEditModal = false;
        this.editingRec = null;
        this.render();
      });
    }

    const btnCancelEdit = this.querySelector('#btn-cancel-edit');
    if (btnCancelEdit) {
      btnCancelEdit.addEventListener('click', () => {
        this.showEditModal = false;
        this.editingRec = null;
        this.render();
      });
    }

    const btnSaveEdit = this.querySelector('#btn-save-edit');
    if (btnSaveEdit) {
      btnSaveEdit.addEventListener('click', () => {
        this.saveEngineeringEdit();
      });
    }
  }

  private async handleRequestCorrection(requests: any[]) {
    if (!requests || requests.length === 0) return;
    const ctx = contextStore.get() as any;
    const p = (window as any).app?.platform;
    if (p?.minutaService && this.currentMinuta) {
      this.currentMinuta = await p.minutaService.requestCorrection(ctx, this.currentMinuta.id, requests);
      this.minutaRevisions = await p.minutaService.getRevisions(ctx, this.currentMinuta.id);
      this.minutaCorrections = await p.minutaService.getCorrectionRequests(ctx, this.currentMinuta.id);
    }
    this.showCorrectionModal = false;
    this.render();
  }

  private viewHistoricalPdf(revId: string | null) {
    if (!revId) return;
    const rev = this.minutaRevisions.find(r => r.id === revId);
    if (!rev) return;
    
    this.snapshotData = {
      id: rev.snapshotId,
      type: 'MINUTA',
      revision: rev.revisionNumber,
      createdAt: rev.createdAt,
      data: rev.snapshotData,
      documentNumber: this.currentMinuta?.trackingNumber
    };
    
    this.showHistoryModal = false;
    this.hasGenerated = true;
    this.render();
  }
}

customElements.define("app-recommendations-draft-page", AppRecommendationsDraftPage);
